/**
 * Edition DTOs (fase 2, steg 2 — second delivery).
 *
 * ---------------------------------------------------------------------------------------------
 * AN EDITION IS ONE YEAR OF A FESTIVAL, AND EVERYTHING OPERATIONAL HANGS ON IT.
 *
 * The build plan: "editions – én årgang av en festival (f.eks. God Nok 2027). Alt operasjonelt
 * henger på edition. Kloning av struktur fra forrige edition." Decision 0010 settled where it
 * lives: inside the festival, never under Administrer. An organisation is a company; an edition is
 * an edition of an event.
 *
 * SORTED NEWEST FIRST, AND THAT IS NOT A PREFERENCE. `ix_editions__org_festival_year` is
 * `(organization_id, festival_id, year DESC, id DESC)`. Somebody opening a festival in March is
 * working on this year or planning the next one; the 2019 edition is history they scroll to. The
 * cursor must match that index or it is not used.
 *
 * THE NAME IS UNIQUE PER FESTIVAL, THE YEAR IS NOT. `ux_editions__festival_name` is on
 * `(organization_id, festival_id, name)` among rows that are not deleted — there is deliberately
 * no unique index on `year`. A festival that runs a winter and a summer edition of 2027 is a real
 * thing, and the schema was built to allow it. So the name is what a person picks it out by, and
 * the year is what the list is ordered by.
 *
 * TIMEZONE IS ON THE EDITION AS WELL AS THE FESTIVAL, ON PURPOSE. A festival that moves venue for
 * one year moves timezone with it, and every get-in, curfew and shift on that edition has to be
 * read in the zone the edition actually happened in — not the zone the organisation is registered
 * in, and not the reader's. It defaults from the festival at create and can then diverge.
 *
 * WHAT IS NOT HERE YET. The clone (`cloned_from_edition_id` exists in the schema and is not
 * written by this contract) and the year picker are the delivery after this one. Structure is
 * copied by KEY — `ux_departments__scope_key` is `(organization_id, edition_id, key)` with
 * `nullsNotDistinct`, so the org-level tree is `edition_id is null` and an edition's own tree
 * carries its id. That is the mechanism; the shape of the request that triggers it is not decided.
 * ---------------------------------------------------------------------------------------------
 */
import { z } from 'zod';

import { editionStatusSchema } from '../enums.js';
import { cursorQuerySchema, page } from '../http/pagination.js';
import { memberPersonIdsSchema } from './edition-members.js';

export const EDITION_SORT_KEYS = ['year'] as const;
export type EditionSortKey = (typeof EDITION_SORT_KEYS)[number];

/** A calendar date with no time and no zone: `starts_on` and `ends_on` are `date`, not timestamps. */
const dateOnly = z.iso.date();

export const editionDtoSchema = z
  .object({
    id: z.uuid(),
    festivalId: z.uuid(),
    /** Unique within the festival. "God Nok 2027", not "2027". */
    name: z.string(),
    year: z.number().int().min(1900).max(2200),
    /**
     * Both nullable, and both nullable independently: a festival books a year before it knows the
     * dates, and `ck_editions__date_order` only asks that an end is not before a start.
     */
    startsOn: dateOnly.nullable(),
    endsOn: dateOnly.nullable(),
    timezone: z.string(),
    /**
     * NÅR FESTIVALDØGNET BEGYNNER (rettelse B12, Atles avgjørelse 17. september).
     *
     * Tallet sto som `DAY_START_HOUR = 6` i en komponent, altså som KODE — prinsipp 9 brutt på den
     * mest konkrete måten. En klubb som spiller til 06:00 og rigger ned til 09:00 har et annet døgn
     * enn en familiefestival som begynner 10:00, og begge er ekte.
     *
     * ÉN VERDI OG IKKE TO: døgnet er 24 timer langt uansett hvor det begynner. «06–02» er ikke tjue
     * timer, det er det samme døgnet tegnet fra klokka seks.
     */
    dayStartHour: z.number().int().min(0).max(23),
    /**
     * KAN DEN SOM FÅR EN VAKT TAKKE NEI (201.14, 0357)? Atle: «i første omgang tildeler vi bare en
     * vakt, og den som mottar må akseptere det uten valg å takke nei.» Standarden er derfor av.
     */
    shiftDeclineAllowed: z.boolean(),
    status: editionStatusSchema,
    /** Set when this edition was cloned from another. Read-only here; the clone is a later step. */
    clonedFromEditionId: z.uuid().nullable(),
  })
  .meta({ id: 'Edition' });

export const listEditionsQuerySchema = cursorQuerySchema(EDITION_SORT_KEYS)
  .extend({
    /**
     * DESCENDING, AND NOT MERELY BY DEFAULT.
     *
     * The header above says newest first is the index rather than a preference, and then the first
     * version of this schema did not say so to the parser: `cursorQuerySchema` defaults `direction`
     * to `'asc'`, and nothing here overrode it. `GET /v1/editions` with no parameters served the
     * OLDEST edition first — the opposite of what six lines of comment claimed.
     *
     * Found by the agent building the API, which did the right thing with it: refused to paper
     * over it in the service, where the code would then have disagreed with its own contract, and
     * pinned the wrong behaviour with a test so the gap was loud rather than quiet.
     *
     * `z.literal('desc')` rather than a default, so `?direction=asc` is a 400 instead of a second
     * cursor shape nothing asks for. `activity` and `inbox` lock theirs the same way.
     */
    direction: z.literal('desc').default('desc'),
    /** Absent means every edition of the festival. The festival page always sends one. */
    festivalId: z.uuid().optional(),
    /**
     * Absent means every status, ARCHIVED INCLUDED, and the UI does not hide them either.
     *
     * This said "archived editions are excluded by the UI" until the UI was built, and that turned
     * out to be three bad ideas at once: the parameter carries one value, so there is no "anything
     * but archived" to ask for; filtering a cursor page in the browser silently shortens it; and
     * nothing in this pass can archive an edition, so a hidden row would have no way back. An
     * archived edition renders with a badge saying so.
     */
    status: editionStatusSchema.optional(),
  })
  .meta({ id: 'ListEditionsQuery' });

export const editionListResponseSchema = page(editionDtoSchema, 'EditionListResponse');

export const createEditionRequestSchema = z
  .object({
    festivalId: z.uuid(),
    name: z.string().trim().min(1).max(120),
    year: z.number().int().min(1900).max(2200),
    startsOn: dateOnly.nullish(),
    endsOn: dateOnly.nullish(),
    /**
     * Defaulted from the festival when omitted, not from the organisation. The festival is the
     * thing with a physical location; an organisation can run events in three countries.
     */
    timezone: z.string().min(1).max(64).optional(),
    /** Utelatt: 6, som er standarden kolonnen har. Se DTO-en for hvorfor bare ett tall. */
    dayStartHour: z.number().int().min(0).max(23).optional(),
    /** A new year starts in `planning`. Nothing else would be true. */
    status: editionStatusSchema.optional(),
    /**
     * HVOR OPPSETTET KOMMER FRA — ET EKSPLISITT VALG (sak 174, QA13 runde 3).
     *
     * Atle: «Ny årgang: «Kopier fra forrige årgang» (forvalgt) / «Kopier fra organisasjonen» /
     * «Start tom».»
     *
     * Til nå ARVET en ny årgang uten å spørre: `app.seed_edition_setup` kopierte fra forrige
     * årgang av samme festival hvis den fantes, ellers bare systemverdiene. Regelen var riktig og
     * usynlig, og en usynlig regel er den man oppdager når den gjorde noe man ikke ville.
     *
     * DE TRE BETYR:
     *   · `previous` — forrige årgang av SAMME festival. Forvalgt, fordi det er det man vil ha ni
     *     ganger av ti. Finnes ingen forrige, er den identisk med `empty` pluss systemverdiene.
     *   · `organization` — organisasjonens eget avdelingstre. K-1 sier at det IKKE skjer av seg
     *     selv (beslutning 0023: settene er atskilte), og dette er den ene veien det kan be om.
     *   · `empty` — bare systemverdiene, altså de radene et år ikke kan eksistere uten: Artist,
     *     Ledelse, Stab, Frivillig, Gjest og On/Off stage.
     *
     * Utelatt betyr `previous`, så hver eksisterende kaller — klonen, testene, importen — er
     * uendret. En ny påkrevd verdi her ville vært en stille atferdsendring for dem alle.
     */
    setupFrom: z.enum(['previous', 'organization', 'empty']).optional(),
    /**
     * «HVEM ER MED?» (QA13 punkt 32). Medlemmene av organisasjonen som er med i årgangen, som
     * personens id. Hver blir en deltakelsesrad med merkelappen `member`, i samme transaksjon som
     * årgangen. Utelatt eller tom: ingen medlemmer — ingen kommer inn av seg selv lenger.
     */
    memberPersonIds: memberPersonIdsSchema.optional(),
  })
  .refine((body) => body.startsOn == null || body.endsOn == null || body.endsOn >= body.startsOn, {
    message: 'The end date cannot be before the start date.',
    path: ['endsOn'],
  })
  .meta({ id: 'CreateEditionRequest' });

/**
 * Renaming, dating, moving zone, changing status — everything except which festival it belongs to.
 *
 * NO `festivalId`. An edition that changed festival would take its departments, tasks, threads and
 * performances with it into a different event's history, and nothing in the product could show
 * that it had happened. If an edition is under the wrong festival it was created wrong; making a
 * new one is cheap and leaves a record that the old one was deleted.
 *
 * `clonedFromEditionId` is likewise absent: it records what happened, and a record you can edit
 * afterwards is not a record.
 *
 * At least one field must be present; a PATCH that changes nothing is a request nobody meant.
 */
export const updateEditionRequestSchema = z
  .object({
    name: z.string().trim().min(1).max(120).optional(),
    year: z.number().int().min(1900).max(2200).optional(),
    startsOn: dateOnly.nullish(),
    endsOn: dateOnly.nullish(),
    timezone: z.string().min(1).max(64).optional(),
    dayStartHour: z.number().int().min(0).max(23).optional(),
    shiftDeclineAllowed: z.boolean().optional(),
    status: editionStatusSchema.optional(),
  })
  .refine((body) => Object.keys(body).length > 0, {
    message: 'At least one field must be present.',
  })
  .refine((body) => body.startsOn == null || body.endsOn == null || body.endsOn >= body.startsOn, {
    message: 'The end date cannot be before the start date.',
    path: ['endsOn'],
  })
  .meta({ id: 'UpdateEditionRequest' });

export type EditionDto = z.infer<typeof editionDtoSchema>;
export type ListEditionsQuery = z.infer<typeof listEditionsQuerySchema>;
export type EditionListResponse = z.infer<typeof editionListResponseSchema>;
export type CreateEditionRequest = z.infer<typeof createEditionRequestSchema>;
export type UpdateEditionRequest = z.infer<typeof updateEditionRequestSchema>;

/* ============================================================================================
 * SAK 182 — «LEGG TIL ANSATTE» PÅ ÅRGANGENS PERSONER
 *
 * Atle, 21. september: «Årgangens Personer: knapp «Legg til ansatte» som lister medlemmer uten
 * tilgang til årgangen. Avkryssing per person, og per person valget «Bare denne årgangen» / «Alle
 * årganger av festivalen (tidligere og framtidige)». Den som legges til her får både tilgang og
 * står på bemanningen.»
 * ============================================================================================ */

/**
 * De to valgene, ordrett fra ordren.
 *
 * TO TABELLER OG IKKE ET FLAGG: `edition` skriver `edition_roles` (per årgang, finnes fra før),
 * `festival` skriver `festival_access` (per festival, «også framtidige»). Den andre KAN ikke være
 * rader i den første — én rad per årgang dekker de årgangene som finnes i dag og ingen av dem som
 * lages i morgen, og da faller personen stille ut av festivalen sin ved neste årgang.
 */
export const festivalAccessScopeSchema = z
  .enum(['edition', 'festival'])
  .meta({ id: 'FestivalAccessScope' });

/** Én person i lista «medlemmer uten tilgang til årgangen». */
export const accessCandidateDtoSchema = z
  .object({
    personId: z.uuid(),
    displayName: z.string(),
    email: z.string().nullable(),
    /** Rollen i huset. Lista er medlemmer, så den forklarer hvem personen er. */
    orgRole: z.string(),
    jobTitle: z.string().nullable(),
    /** Står hen allerede på bemanningen? Da legges bare tilgangen til, ikke raden. */
    onStaffing: z.boolean(),
  })
  .meta({ id: 'AccessCandidate' });

export const accessCandidateListResponseSchema = z
  .object({ items: z.array(accessCandidateDtoSchema) })
  .meta({ id: 'AccessCandidateListResponse' });

/**
 * Én forespørsel for hele avkryssinga, ikke én per person.
 *
 * Atle beskrev én knapp med flere hakene i. Seks forespørsler for seks haker ville gitt seks
 * halvveis utfall å forklare når den fjerde feiler; én gir ett svar og én revisjonshistorie.
 */
export const grantEditionAccessRequestSchema = z
  .object({
    people: z
      .array(
        z.object({
          personId: z.uuid(),
          scope: festivalAccessScopeSchema,
        }),
      )
      .min(1)
      .max(200),
  })
  .meta({ id: 'GrantEditionAccessRequest' });

export const grantEditionAccessResponseSchema = z
  .object({
    /** Antall personer som fikk tilgang de ikke hadde. */
    granted: z.number().int(),
    /** Antall som ble satt på bemanningen. Lavere enn `granted` når noen alt sto der. */
    staffed: z.number().int(),
  })
  .meta({ id: 'GrantEditionAccessResponse' });

export type FestivalAccessScope = z.infer<typeof festivalAccessScopeSchema>;
export type AccessCandidateDto = z.infer<typeof accessCandidateDtoSchema>;
export type AccessCandidateListResponse = z.infer<typeof accessCandidateListResponseSchema>;
export type GrantEditionAccessRequest = z.infer<typeof grantEditionAccessRequestSchema>;
export type GrantEditionAccessResponse = z.infer<typeof grantEditionAccessResponseSchema>;
